import { useState, useEffect, useRef } from "react";
import { AdminLayout } from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Presentation, Search, ChevronDown, ChevronUp, Copy, Loader2,
  Building2, Mail, User, CheckCircle, ArrowRight, ArrowLeft, Sparkles
} from "lucide-react";

const QUESTIONS = [
  "Can you provide an overview of your company's industry and the specific market segment you operate in?",
  "What are the primary products or services your company offers, and how do they differentiate you from competitors?",
  "How does your company generate revenue, and what are your main sources of income?",
  "What are the key drivers for growth in your industry, and how does your company align with these trends?",
  "Can you describe your target customer profile(s) and the typical buyer's journey within your industry?",
  "What are the main challenges or pain points your company is currently facing in terms of operations, sales, or customer satisfaction?",
  "How do you currently manage your internal processes, such as inventory management, order fulfilment, and customer relationship management?",
  "What are your company's short-term and long-term strategic goals, and how do you plan to achieve them?",
  "Can you walk me through your current technology infrastructure, including any existing software systems or tools you use?",
  "How do you handle data management and analysis within your organization?",
  "What are your company's strengths and weaknesses compared to your competitors?",
  "Are there any specific regulatory or compliance requirements that your company needs to adhere to?",
  "How do you measure success within your organization, and what KPIs are most important to you?",
  "Can you provide insight into your company's organizational structure and decision-making processes?",
  "How do you currently handle customer service and support, and what are your customer satisfaction goals?",
  "What are your company's plans for growth and expansion in the coming years?",
  "Are there any emerging trends or technologies in your industry that you're particularly interested in exploring?",
  "Can you share any recent success stories or milestones that your company has achieved?",
  "How do you stay ahead of the competition and adapt to changes in the market landscape?",
  "What are your company's core values and mission, and how do they guide your decision-making?",
  "Can you describe any recent challenges or obstacles your company has faced and how you've overcome them?",
  "How do you currently handle employee training and development, particularly in terms of new technologies or processes?",
  "What are the biggest opportunities for improvement or innovation within your company?",
  "How do you envision technology playing a role in the future growth and success of your business?",
  "Are there any specific areas where you feel your company could benefit from additional support or expertise?",
  "Can you provide insights into your company's customer acquisition and retention strategies?",
  "What are your company's core values and mission, and how do they guide your decision-making?",
  "Is there anything else you would like to share about your company or any additional information you think would be helpful for us to know?",
];

export default function PresentationGenerator() {
  const [leads, setLeads] = useState([]);
  const [selectedLead, setSelectedLead] = useState(null);
  const [leadSearch, setLeadSearch] = useState("");
  const [showLeadDropdown, setShowLeadDropdown] = useState(false);
  const [proposedSolution, setProposedSolution] = useState("");
  const [alignmentOverview, setAlignmentOverview] = useState("");
  const [responses, setResponses] = useState({});
  const [generating, setGenerating] = useState(false);
  const [generatedPrompt, setGeneratedPrompt] = useState("");
  const [step, setStep] = useState(1); // 1: Lead, 2: Intro, 3: Questions (1-14), 4: Questions (15-28), 5: Result
  const [history, setHistory] = useState([]);
  const promptRef = useRef(null);

  useEffect(() => {
    loadLeads();
    loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadLeads = async () => {
    try {
      const res = await api.get("/leads?status=all");
      setLeads(res.data || []);
    } catch (error) {
      toast.error("Failed to load leads");
    }
  };

  const loadHistory = async () => {
    try {
      const res = await api.get("/presentations/history");
      setHistory(res.data || []);
    } catch (error) {
      toast.error("Failed to load history");
    }
  };

  const filteredLeads = leads.filter((l) => {
    const q = leadSearch.toLowerCase();
    return !q || (l.name || "").toLowerCase().includes(q) || (l.email || "").toLowerCase().includes(q) || (l.company || "").toLowerCase().includes(q);
  });

  const handleGenerate = async () => {
    if (!selectedLead) { toast.error("Select a lead first"); return; }
    setGenerating(true);
    try {
      const res = await api.post("/presentations/generate", {
        lead_id: selectedLead.id,
        proposed_solution: proposedSolution,
        alignment_overview: alignmentOverview,
        responses
      });
      setGeneratedPrompt(res.data.prompt);
      setStep(5);
      loadHistory();
      toast.success("Gamma.app prompt generated!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to generate prompt");
    }
    setGenerating(false);
  };

  const copyPrompt = async () => {
    try {
      await navigator.clipboard.writeText(generatedPrompt);
      toast.success("Prompt copied to clipboard!");
    } catch {
      const ta = document.createElement("textarea");
      ta.value = generatedPrompt;
      ta.style.cssText = "position:fixed;left:-9999px";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      toast.success("Prompt copied!");
    }
  };

  const answeredCount = Object.values(responses).filter((v) => v.trim()).length;
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-3" data-testid="presentation-title">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center">
                <Presentation className="w-5 h-5 text-white" />
              </div>
              Presentation Generator
            </h1>
            <p className="text-sm text-slate-500 mt-1">Generate Gamma.app prompts for prospect alignment presentations</p>
          </div>
          {step > 1 && step < 5 && (
            <div className="text-sm text-slate-500 bg-slate-100 px-3 py-1.5 rounded-full">
              Step {step} of 4
            </div>
          )}
        </div>

        {/* Progress Bar */}
        {step < 5 && (
          <div className="flex gap-1">
            {[1, 2, 3, 4].map((s) => (
              <div key={s} className={`h-1.5 flex-1 rounded-full transition-colors ${s <= step ? "bg-indigo-500" : "bg-slate-200"}`} />
            ))}
          </div>
        )}

        {/* Step 1: Select Lead */}
        {step === 1 && (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-5" data-testid="step-select-lead">
            <h2 className="text-lg font-semibold text-slate-900">Select a Lead / Prospect</h2>
            <p className="text-sm text-slate-500">Choose the prospect for whom you want to generate the alignment presentation.</p>

            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                value={leadSearch}
                onChange={(e) => setLeadSearch(e.target.value)}
                onFocus={() => setShowLeadDropdown(true)}
                onBlur={() => setTimeout(() => setShowLeadDropdown(false), 200)}
                placeholder="Search by name, email or company..."
                className="pl-10"
                data-testid="lead-search-input"
              />

              {showLeadDropdown && (
                <div className="absolute z-10 mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-lg max-h-64 overflow-y-auto" data-testid="lead-search-results">
                  {filteredLeads.length === 0 ? (
                    <div className="p-4 text-sm text-slate-400 text-center">No leads found. Create one in Lead Masters first.</div>
                  ) : filteredLeads.map((l) => (
                    <button key={l.id} onMouseDown={(e) => { e.preventDefault(); setSelectedLead(l); setLeadSearch(""); setShowLeadDropdown(false); }}
                      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-indigo-50 text-left transition-colors border-b border-slate-50 last:border-0"
                      data-testid={`select-lead-${l.id}`}>
                      {l.logo_url ? (
                        <img src={`${API_URL}${l.logo_url}`} alt="" className="w-9 h-9 rounded-lg border object-contain bg-white" />
                      ) : (
                        <div className="w-9 h-9 rounded-lg bg-indigo-100 flex items-center justify-center text-sm font-bold text-indigo-600">
                          {(l.name || "?")[0].toUpperCase()}
                        </div>
                      )}
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">{l.name}</p>
                        <p className="text-xs text-slate-400 truncate">{l.company || l.email}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {selectedLead && (
              <div className="bg-indigo-50 rounded-xl p-4 border border-indigo-100 flex items-center gap-4" data-testid="selected-lead-card">
                <div className="w-12 h-12 rounded-xl bg-indigo-200 flex items-center justify-center text-lg font-bold text-indigo-700 shrink-0">
                  {(selectedLead.name || "?")[0].toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-indigo-900">{selectedLead.name}</p>
                  <div className="flex gap-4 text-xs text-indigo-600 mt-0.5">
                    <span className="flex items-center gap-1"><Building2 className="w-3 h-3" />{selectedLead.company || "N/A"}</span>
                    <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{selectedLead.email}</span>
                  </div>
                </div>
                <Button size="sm" onClick={() => setStep(2)} className="bg-indigo-600 hover:bg-indigo-700 gap-1" data-testid="next-to-intro">
                  Next <ArrowRight className="w-3.5 h-3.5" />
                </Button>
              </div>
            )}

            {/* Recent History */}
            {history.length > 0 && (
              <div className="mt-6">
                <h3 className="text-sm font-semibold text-slate-700 mb-3">Recent Generations</h3>
                <div className="space-y-2">
                  {history.slice(0, 5).map((h) => (
                    <div key={h.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-100">
                      <div>
                        <p className="text-sm font-medium text-slate-800">{h.lead_name} — {h.lead_company || "N/A"}</p>
                        <p className="text-xs text-slate-400">{new Date(h.created_at).toLocaleDateString()}</p>
                      </div>
                      <Button variant="outline" size="sm" onClick={() => { setGeneratedPrompt(h.prompt); setStep(5); }} className="gap-1 text-xs">
                        View Prompt
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 2: Intro Fields */}
        {step === 2 && (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-5" data-testid="step-intro-fields">
            <h2 className="text-lg font-semibold text-slate-900">Alignment Context</h2>
            <p className="text-sm text-slate-500">Provide the proposed solution and alignment overview for <strong>{selectedLead?.name}</strong>.</p>

            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">Proposed Solution from ABSOLIN</Label>
                <Textarea
                  value={proposedSolution}
                  onChange={(e) => setProposedSolution(e.target.value)}
                  placeholder="e.g., SAP Business One implementation with custom integrations for inventory and CRM..."
                  rows={3}
                  data-testid="proposed-solution-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">Overview of the Alignment Call</Label>
                <Textarea
                  value={alignmentOverview}
                  onChange={(e) => setAlignmentOverview(e.target.value)}
                  placeholder="e.g., Initial alignment meeting with CTO and Operations Head to discuss digitization roadmap..."
                  rows={3}
                  data-testid="alignment-overview-input"
                />
              </div>
            </div>

            <div className="flex justify-between pt-4 border-t">
              <Button variant="outline" onClick={() => setStep(1)} className="gap-1">
                <ArrowLeft className="w-3.5 h-3.5" /> Back
              </Button>
              <Button onClick={() => setStep(3)} className="bg-indigo-600 hover:bg-indigo-700 gap-1" data-testid="next-to-questions">
                Next: Alignment Questions <ArrowRight className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Questions 1-14 */}
        {step === 3 && (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-5" data-testid="step-questions-1">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Alignment Questions (1-14)</h2>
                <p className="text-sm text-slate-500 mt-0.5">{answeredCount} of 28 answered</p>
              </div>
            </div>

            <div className="space-y-4">
              {QUESTIONS.slice(0, 14).map((q, idx) => (
                <QuestionField key={idx} num={idx + 1} question={q} value={responses[String(idx + 1)] || ""}
                  onChange={(val) => setResponses({ ...responses, [String(idx + 1)]: val })} />
              ))}
            </div>

            <div className="flex justify-between pt-4 border-t">
              <Button variant="outline" onClick={() => setStep(2)} className="gap-1">
                <ArrowLeft className="w-3.5 h-3.5" /> Back
              </Button>
              <Button onClick={() => setStep(4)} className="bg-indigo-600 hover:bg-indigo-700 gap-1" data-testid="next-to-questions-2">
                Questions 15-28 <ArrowRight className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 4: Questions 15-28 */}
        {step === 4 && (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-5" data-testid="step-questions-2">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Alignment Questions (15-28)</h2>
                <p className="text-sm text-slate-500 mt-0.5">{answeredCount} of 28 answered</p>
              </div>
            </div>

            <div className="space-y-4">
              {QUESTIONS.slice(14, 28).map((q, idx) => (
                <QuestionField key={idx + 14} num={idx + 15} question={q} value={responses[String(idx + 15)] || ""}
                  onChange={(val) => setResponses({ ...responses, [String(idx + 15)]: val })} />
              ))}
            </div>

            <div className="flex justify-between pt-4 border-t">
              <Button variant="outline" onClick={() => setStep(3)} className="gap-1">
                <ArrowLeft className="w-3.5 h-3.5" /> Back
              </Button>
              <Button onClick={handleGenerate} disabled={generating}
                className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 gap-2 px-6"
                data-testid="generate-prompt-btn">
                {generating ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Generating...</>
                ) : (
                  <><Sparkles className="w-4 h-4" /> Generate Gamma.app Prompt</>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Step 5: Generated Prompt */}
        {step === 5 && (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-5" data-testid="step-result">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-emerald-500" /> Gamma.app Prompt Ready
                </h2>
                <p className="text-sm text-slate-500 mt-0.5">Copy this prompt and paste it into <a href="https://gamma.app" target="_blank" rel="noopener noreferrer" className="text-indigo-600 underline">gamma.app</a> to generate your presentation.</p>
              </div>
              <Button onClick={copyPrompt} className="bg-indigo-600 hover:bg-indigo-700 gap-2" data-testid="copy-prompt-btn">
                <Copy className="w-4 h-4" /> Copy Prompt
              </Button>
            </div>

            <div ref={promptRef} className="bg-slate-900 text-slate-100 rounded-xl p-5 font-mono text-sm leading-relaxed max-h-[60vh] overflow-y-auto whitespace-pre-wrap" data-testid="generated-prompt">
              {generatedPrompt}
            </div>

            <div className="flex items-center gap-3 p-4 bg-amber-50 rounded-xl border border-amber-200">
              <Sparkles className="w-5 h-5 text-amber-600 shrink-0" />
              <div className="text-sm text-amber-800">
                <strong>Next Steps:</strong> Open <a href="https://gamma.app" target="_blank" rel="noopener noreferrer" className="underline">gamma.app</a>, click "Create new", select "Paste in text", and paste this prompt. Gamma will generate a professional PPTX presentation.
              </div>
            </div>

            <div className="flex gap-3 pt-4 border-t">
              <Button variant="outline" onClick={() => { setStep(1); setGeneratedPrompt(""); setSelectedLead(null); setProposedSolution(""); setAlignmentOverview(""); setResponses({}); }} className="gap-1" data-testid="new-presentation-btn">
                <Presentation className="w-4 h-4" /> New Presentation
              </Button>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

function QuestionField({ num, question, value, onChange }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className={`rounded-xl border transition-colors ${value.trim() ? "border-emerald-200 bg-emerald-50/30" : "border-slate-200"} p-3`}>
      <button type="button" onClick={() => setExpanded(!expanded)} className="w-full flex items-start gap-3 text-left" data-testid={`question-toggle-${num}`}>
        <span className={`w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold shrink-0 mt-0.5 ${value.trim() ? "bg-emerald-500 text-white" : "bg-slate-200 text-slate-600"}`}>
          {value.trim() ? <CheckCircle className="w-3.5 h-3.5" /> : num}
        </span>
        <p className="text-sm text-slate-800 flex-1">{question}</p>
        {expanded ? <ChevronUp className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" /> : <ChevronDown className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />}
      </button>
      {expanded && (
        <div className="mt-3 pl-10">
          <Textarea
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Enter the prospect's response..."
            rows={2}
            className="text-sm"
            data-testid={`question-input-${num}`}
          />
        </div>
      )}
    </div>
  );
}
